import { OPERAND_TYPE_VALUE } from '@atlaskit/jql-ast';
import type { IntlShape } from '@atlassian/jira-intl';
import type { SelectOption } from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type { useHydratedValues$data } from '../../__generated__/useHydratedValues.graphql';
import { removeSurroundingQuotes } from '../../../../../../../utils/remove-surrounding-quotes.tsx';
import { getOption as getCommonOption } from '../../../props-resolver/common/getCommonProps.tsx';
import { getOption as getIssueTypeOption } from '../../../props-resolver/issue-type/getIssueTypeProps.tsx';
import { getOption as getProjectOption } from '../../../props-resolver/project/getProjectProps.tsx';
import { getOption as getSprintOption } from '../../../props-resolver/sprint/getSprintProps.tsx';

export const getValue = (
	data: useHydratedValues$data,
	fieldJqlTerm: string,
	valueJqlTerm: string,
	intl: IntlShape,
): SelectOption | undefined => {
	const matchingField = (
		data?.jira?.jqlBuilder?.hydrateJqlQuery ?? data?.jira?.jqlBuilder?.hydrateJqlQueryForFilter
	)?.fields?.find(
		(field) => field.jqlTerm && field.jqlTerm.toLowerCase() === fieldJqlTerm.toLowerCase(),
	);

	const fieldData = matchingField?.values?.find(
		(value) =>
			value?.jqlTerm &&
			(value.jqlTerm === valueJqlTerm ||
				value.jqlTerm.toLowerCase() === removeSurroundingQuotes(valueJqlTerm).toLowerCase()),
	);

	if (fieldData?.error) {
		return {
			label: removeSurroundingQuotes(valueJqlTerm),
			optionType: 'option',
			value: valueJqlTerm,
			invalid: true,
			itemType: OPERAND_TYPE_VALUE,
		};
	}

	if (fieldData?.values?.length) {
		const [hydratedFieldValue] = fieldData.values;
		if (hydratedFieldValue) {
			switch (hydratedFieldValue.__typename) {
				case 'JiraJqlUserFieldValue': {
					return {
						label: hydratedFieldValue.displayName,
						value: hydratedFieldValue.jqlTerm,
						optionType: 'avatar',
						avatar: hydratedFieldValue.user?.picture,
						itemType: OPERAND_TYPE_VALUE,
					};
				}
				case 'JiraJqlGroupFieldValue': {
					return {
						label: hydratedFieldValue.displayName,
						value: hydratedFieldValue.jqlTerm,
						optionType: 'avatar',
						itemType: OPERAND_TYPE_VALUE,
						isGroup: true,
					};
				}
				case 'JiraJqlIssueTypeFieldValue': {
					return getIssueTypeOption(hydratedFieldValue, OPERAND_TYPE_VALUE);
				}
				case 'JiraJqlProjectFieldValue': {
					return getProjectOption(hydratedFieldValue, OPERAND_TYPE_VALUE);
				}
				case 'JiraJqlSprintFieldValue': {
					return getSprintOption(hydratedFieldValue, intl.formatMessage, OPERAND_TYPE_VALUE);
				}
				default: {
					return getCommonOption(hydratedFieldValue, OPERAND_TYPE_VALUE);
				}
			}
		}
	}

	return undefined;
};
